import { inject } from 'aurelia-framework';
import { NewInstance } from 'aurelia-dependency-injection';
import { ValidationController, validateTrigger, ValidationRules } from 'aurelia-validation';
import { Router } from 'aurelia-router';
import { UserService } from '../Advertiser.SharedPortal/services/user-service';
import { LoggerService } from '../Advertiser.SharedPortal/services/logger-service';
import { StatusService } from '../Advertiser.SharedPortal/services/status-service';
import { MultiObserverService } from '../Advertiser.SharedPortal/services/multi-observer-service';
import { BrowserStorageService } from '../Advertiser.SharedPortal/services/browser-storage-service';

const config = require('../../config.js');

@inject(
    Router,
    UserService,
    NewInstance.of(ValidationController),
    LoggerService,
    StatusService,
    MultiObserverService,
    BrowserStorageService
)
export class ResetPassword {
    constructor(
        router,
        userService,
        controller,
        loggerService,
        status,
        multiObserver,
        browserStorage
    ) {
        this.router = router;
        this.userService = userService;
        this.controller = controller;
        this.controller.validateTrigger = validateTrigger.manual;
        this.logger = loggerService;
        this.status = status;
        this.multiObserver = multiObserver;
        this.browserStorage = browserStorage;

        this.errorMessage = '';
        this.submitted = false;
        this.emailAddress = '';
        this.deltyCode = '';
        this.newPassword = '';
        this.newPassword2 = '';
        this.isSubmitting = false;

        this.myKeypressCallback = this.keypressInput.bind(this);
    }

    activate(params, routerConfig) {
        let that = this;

        // Only handle email-based resets with confirmation codes
        this.deltyCode = params.deltycode ? decodeURIComponent(params.deltycode) : '';
        this.emailAddress = params.email ? decodeURIComponent(params.email) : '';

        this.configStatus();

        // Observe all required fields for email-based reset
        this.multiObserver.observe(
            this,
            ['emailAddress', 'deltyCode', 'newPassword', 'newPassword2'],
            function() {
                that.status.dirty();
            }
        );
        window.addEventListener('keypress', this.myKeypressCallback, false);
    }

    deactivate() {
        window.removeEventListener('keypress', this.myKeypressCallback);
    }

    keypressInput(e) {
        if (e.key === 'Enter') {
            this.onSubmit();
        }
    }

    redirectToPortal() {
        this.userService.redirectToPortal();
    }

    configStatus() {
        this.status.texts = [
            '',
            'Reset Password',
            'Reset Password',
            'Resetting Password',
            '',
            '',
            'Password Reset',
            'Error'
        ];
        this.status.clean();
    }

    onSubmit() {
        // Prevent duplicate submissions
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.errors = [];

        let that = this;
        this.controller.validate().then(results => {
            if (!results.valid) {
                // Show validation errors instead of attempting authentication
                that.isSubmitting = false;
                that.status.set(
                    that.status.statuses.error,
                    that.status.statuses.clean,
                    config.buttonReset
                );
                return;
            }

            that.status.working();
            that.errorMessage = '';
            that.controller.reset();

            that.userService
                .completeNewPassword(that.deltyCode, that.emailAddress, that.newPassword)
                .then(response => {
                    console.log('completeNewPassword response', response);
                    // Response is wrapped in an array by Promise.all(), so access the first element
                    const result = Array.isArray(response) ? response[0] : response;

                    if (result?.result !== 'SUCCESS') {
                        that.errorMessage =
                            'There was an issue setting your password. Please try again, or contact Delty Support.';
                        that.isSubmitting = false;
                        that.status.set(
                            that.status.statuses.error,
                            that.status.statuses.clean,
                            config.buttonReset
                        );
                        return;
                    }

                    // Password reset successful - show success message and redirect to login
                    that.errorMessage = '';
                    that.submitted = true;
                    that.isSubmitting = false;
                    that.status.set(
                        that.status.statuses.success,
                        that.status.statuses.clean,
                        config.buttonReset
                    );

                    // Redirect to login page after showing success message
                    setTimeout(function() {
                        that.router.navigateToRoute('login');
                    }, 2000);
                })
                .catch(error => {
                    that.logger.error('CompleteNewPassword error:', error);
                    that.errorMessage =
                        'Invalid temporary password. Please check your email for the correct temporary password.';
                    that.isSubmitting = false;
                    that.status.set(
                        that.status.statuses.error,
                        that.status.statuses.clean,
                        config.buttonReset
                    );
                });
        });
    }
}

const passwordRegex = /(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[\^\$\*\.\[\]\{\}\(\)\?\-\"\!\@\#\%\&\/\,><':;|_~`]).{8,}/;
const passwordFormatMessage =
    'Passwords must be at least 8 characters including one number, one upper, one lowercase letter, and a special character';
const passwordMatchMessage = 'Re-entered password does not match new password';

// Validation rules for email-based password reset
ValidationRules.ensure(p => p.emailAddress)
    .required()
    .ensure(p => p.emailAddress)
    .email()
    .withMessage('Email address is improperly formated (please ensure it is all lowercase)')
    .ensure(p => p.deltyCode)
    .required()
    .withMessage('Please enter a valid temporary password')
    .ensure(p => p.newPassword)
    .required()
    .ensure(p => p.newPassword)
    .matches(passwordRegex)
    .withMessage(passwordFormatMessage)
    .ensure(p => p.newPassword2)
    .required()
    .withMessage('Re-entered password is required')
    .ensure(p => p.newPassword2)
    .satisfies((value, obj) => value === obj.newPassword)
    .withMessage(passwordMatchMessage)
    .on(ResetPassword);

